import * as React from 'react';

import { Pill } from '@components/common';
import { CheckmarkInCircleSVG, ListEndStreamlineSVG, SkipLogicSVG, SubtractCircleSVG, ViewSVG } from '@components/svgs';
import { find } from '@components/utils';

import { calculateNumber } from '../../utils';
import {
  END_OF_SURVEY_ALLOWING_AUTO_REVIEW,
  END_OF_SURVEY_PREVENTING_AUTO_REVIEW,
  END_OF_SURVEY_WITHOUT_SUBMIT
} from '../SkipLogicSlideOut';
import { RuleOutcome } from '../utils';

const UNTITLED_QUESTION = '(Untitled question)';

interface SkipToQuestionProps {
  tagValue: RuleOutcome['tag_value'];
  questions: ScreenerField[];
}

export const SkipToQuestion: React.FC<SkipToQuestionProps> = ({ tagValue, questions }) => {
  const numericTagValue = Number(tagValue);

  if (
    numericTagValue === END_OF_SURVEY_PREVENTING_AUTO_REVIEW ||
    numericTagValue === END_OF_SURVEY_ALLOWING_AUTO_REVIEW ||
    numericTagValue === END_OF_SURVEY_WITHOUT_SUBMIT
  ) {
    return (
      <div className='flex items-center space-x-1'>
        <ListEndStreamlineSVG />
        End
      </div>
    );
  }

  const nextQuestion = find(questions, (q) => q.id === numericTagValue);
  if (nextQuestion) {
    const questionNumber = calculateNumber(
      questions,
      questions.findIndex((q) => q.id === nextQuestion.id)
    );
    return (
      <div className='flex items-center space-x-1'>
        <ViewSVG />
        <span>
          Q{questionNumber}: {nextQuestion.label || UNTITLED_QUESTION}
        </span>
      </div>
    );
  }

  return 'Unknown question';
};

interface TagTypeIconProps {
  tagType: RuleOutcome['tag_type'];
}

const TagTypeIcon: React.FC<TagTypeIconProps> = ({ tagType }) => {
  switch (tagType) {
    case 'ideal':
      return (
        <div className='flex items-center space-x-1 pl-1'>
          <CheckmarkInCircleSVG className='h-3 w-3' />
          <span>Mark Qualifying</span>
        </div>
      );
    case 'disqualifying':
      return (
        <div className='flex items-center space-x-1 pl-1'>
          <SubtractCircleSVG className='h-3 w-3' />
          <span>Mark Disqualifying</span>
        </div>
      );
    default:
      return (
        <div className='flex items-center space-x-1 pl-1'>
          <SkipLogicSVG className='h-3 w-3' />
          <span>Go To</span>
        </div>
      );
  }
};

interface OutcomeTypePillProps {
  outcome: RuleOutcome;
}

const OutcomeTypePill: React.FC<OutcomeTypePillProps> = ({ outcome }) => (
  <Pill color='white' className='normal-case'>
    <TagTypeIcon tagType={outcome.tag_type} />
  </Pill>
);

interface SkipToPillProps {
  outcome: RuleOutcome;
  questions: ScreenerField[];
}

const SkipToPill: React.FC<SkipToPillProps> = ({ outcome, questions }) => {
  if (outcome.tag_type !== 'skip_to') {
    return null;
  }

  return (
    <Pill color='white'>
      <SkipToQuestion tagValue={outcome.tag_value} questions={questions} />
    </Pill>
  );
};

interface RuleSummaryProps {
  outcome: RuleOutcome;
  questions: ScreenerField[];
}

export const RuleOutcomeSummary: React.FC<RuleSummaryProps> = ({ outcome, questions }) => {
  return (
    <div className='flex items-center space-x-2'>
      <OutcomeTypePill outcome={outcome} />
      <SkipToPill outcome={outcome} questions={questions} />
    </div>
  );
};
